import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { allVideos, categories } from '@/mocks/videos';
import { siteConfig } from '@/mocks/site';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const video = allVideos.find((v) => v.id === Number(id));

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">未找到该影片</h1>
            <p className="text-foreground-500 mb-6">该影片可能已下架或链接无效</p>
            <Link
              to="/"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const videoCategory = categories.find((c) => c.slug === video.category);
  const relatedVideos = allVideos
    .filter((v) => v.category === video.category && v.id !== video.id)
    .slice(0, 6);

  const articleSchema = {
    headline: video.title,
    description: video.description,
    datePublished: `${video.year}-01-01`,
    author: {
      '@type': 'Person',
      name: video.director,
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
  };

  return (
    <>
      <SchemaOrg type="Article" data={articleSchema} />

      <Navbar />

      <main className="pt-16 min-h-screen bg-background-50">
        <section className="py-6 md:py-8 bg-background-900">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: videoCategory?.name || '分类', to: `/category/${video.category}` },
                { label: video.title },
              ]}
            />
          </div>
        </section>

        <section className="py-8 md:py-12">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="w-full lg:w-[300px] shrink-0">
                <div className="rounded-lg overflow-hidden bg-background-200 aspect-[2/3] lg:max-w-[300px]">
                  <img
                    src={video.posterUrl}
                    alt={video.title}
                    title={`${video.title} - 农民影视高清全集在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">{video.title}</h1>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent-100 text-accent-800 text-xs font-semibold rounded-md">
                    <i className="ri-star-fill"></i> {video.rating}
                  </span>
                  <span className="text-sm text-foreground-500">{video.year}</span>
                  <span className="text-sm text-foreground-500">{video.duration}</span>
                  <span className="text-sm text-foreground-500">{video.type === 'movie' ? '电影' : '电视剧'}</span>
                  {videoCategory && (
                    <Link
                      to={`/category/${video.category}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap"
                    >
                      {videoCategory.name}
                    </Link>
                  )}
                </div>

                <p className="text-foreground-600 text-sm leading-relaxed mb-6">{video.description}</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 bg-background-100 rounded-lg">
                  <div>
                    <span className="text-xs text-foreground-400">导演</span>
                    <p className="text-sm text-foreground-800 font-medium mt-0.5">{video.director}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">主演</span>
                    <p className="text-sm text-foreground-800 font-medium mt-0.5">
                      {video.cast.length > 0 ? video.cast.join(' / ') : '暂无'}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">类型</span>
                    <p className="text-sm text-foreground-800 font-medium mt-0.5">{videoCategory?.name || '未知'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-foreground-400">时长 / 集数</span>
                    <p className="text-sm text-foreground-800 font-medium mt-0.5">{video.duration}</p>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap">
                    <i className="ri-play-fill text-lg"></i>
                    立即播放
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 font-medium rounded-full hover:bg-background-200 transition-colors border border-background-300 whitespace-nowrap">
                    <i className="ri-heart-line"></i>
                    收藏
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 font-medium rounded-full hover:bg-background-200 transition-colors border border-background-300 whitespace-nowrap">
                    <i className="ri-share-forward-line"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {relatedVideos.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                同类推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                {relatedVideos.map((v) => (
                  <MovieCard
                    key={v.id}
                    id={v.id}
                    title={v.title}
                    category={v.category}
                    year={v.year}
                    rating={v.rating}
                    type={v.type}
                    posterUrl={v.posterUrl}
                    categoryName={videoCategory?.name}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}